import { z } from 'zod';

export const PACKAGE_LIMITS = {
  archive: 2 * 1024 * 1024,
  expanded: 4 * 1024 * 1024,
  files: 64,
  installed: 16,
} as const;
export const capabilitySchema = z.enum([
  'audio',
  'wireless',
  'network',
  'javascript',
  'mouse',
  'keyboard',
  'storage',
  'time',
]);
export const packagePath = z
  .string()
  .min(1)
  .max(160)
  .refine(
    (path) =>
      /^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(path) &&
      path.split('/').every((part) => part !== '' && part !== '.' && part !== '..'),
    'Use a relative package path without empty, dot or parent segments.',
  );
export const packageManifestSchema = z
  .object({
    format: z.literal('digitiles-extension'),
    formatVersion: z.literal(1),
    apiVersion: z.literal(1),
    id: z
      .string()
      .regex(/^[a-z][a-z0-9.-]{0,79}$/)
      .refine(
        (id) => !id.startsWith('digitiles.'),
        'The digitiles namespace is reserved.',
      ),
    name: z.string().trim().min(1).max(80),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    description: z.string().trim().min(1).max(500),
    publisher: z.string().trim().min(1).max(100),
    homepage: z
      .url()
      .refine((value) => value.startsWith('https://'), 'Use an HTTPS homepage.')
      .optional(),
    capabilities: z
      .array(capabilitySchema)
      .max(8)
      .refine(
        (items) => new Set(items).size === items.length,
        'Capabilities must be unique.',
      ),
    entrypoints: z
      .array(
        packagePath.refine(
          (path) => path.endsWith('.js'),
          'Entrypoints must be JavaScript files.',
        ),
      )
      .min(1)
      .max(8)
      .refine(
        (items) => new Set(items).size === items.length,
        'Entrypoints must be unique.',
      ),
    reloadRequired: z.boolean(),
  })
  .strict();
export type PackageManifest = z.infer<typeof packageManifestSchema>;
export interface PackageContent {
  manifest: PackageManifest;
  files: Record<string, string>;
}
export interface SavedPackage extends PackageContent {
  archive: Uint8Array;
  digest: string;
  installedAt: number;
  enabled: boolean;
}

export function validateContent(
  manifest: unknown,
  files: Record<string, string>,
): PackageContent {
  const parsed = packageManifestSchema.parse(manifest);
  let size = 0;
  if (Object.keys(files).length > PACKAGE_LIMITS.files)
    throw new Error('Too many package files.');
  for (const [path, text] of Object.entries(files)) {
    packagePath.parse(path);
    if (typeof text !== 'string') throw new Error('Invalid package source.');
    size += new TextEncoder().encode(text).byteLength;
    if (size > PACKAGE_LIMITS.expanded)
      throw new Error('Expanded package exceeds 4 MiB.');
  }
  for (const entry of parsed.entrypoints)
    if (!files[entry]?.trim())
      throw new Error(`Missing or empty JavaScript entrypoint: ${entry}`);
  return { manifest: parsed, files };
}
